import { workOrderService } from './workOrderService'
import { workOrderOfflineStorage } from './offlineStorage'
import { apiEndpoints } from '../config/env'

class WorkOrderPrefetchService {
  private isPrefetching = false
  private prefetchAbortController: AbortController | null = null
  private lastPrefetchTimestamps: Map<string, number> = new Map()
  private prefetchInterval = 60 * 1000 * 10 // 30 minutes in milliseconds
  private intervalTimers: Map<string, NodeJS.Timeout> = new Map()
  private prefetchStartTime: number = 0

  /**
   * Prefetch all work orders for today for the logged-in technician
   * This runs in the background to cache work order details for offline use
   * Checks if enough time has passed since last prefetch before running
   */
  async prefetchTodayWorkOrders(technicianId: number, forceRefresh = false): Promise<void> {
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toLocaleString('sv').split(' ')[0]
    const cacheKey = `${technicianId}-${today}`

    // Check if we need to prefetch based on last timestamp
    if (!forceRefresh && !this.shouldPrefetch(cacheKey)) {
      return
    }

    // Prevent multiple concurrent prefetch operations for the same cache key
    if (this.isPrefetching) {
      return
    }

    try {
      this.isPrefetching = true
      this.prefetchStartTime = Date.now()
      this.prefetchAbortController = new AbortController()
      

      // Fetch all work orders for today (all statuses)
      const workOrders = await workOrderService.getFilteredWorkOrders(
        technicianId,
        today,
        'all'
      )


      // Process each work order in parallel with a concurrency limit
      const concurrencyLimit = 3
      const results = []

      for (let i = 0; i < workOrders.length; i += concurrencyLimit) {
        // Check if prefetch was cancelled
        if (this.prefetchAbortController.signal.aborted) {
          break
        }

        const batch = workOrders.slice(i, i + concurrencyLimit)
        const batchPromises = batch.map(workOrder => 
          this.prefetchWorkOrderDetails(workOrder.workOrderID)
        )

        const batchResults = await Promise.allSettled(batchPromises)
        results.push(...batchResults)

        // Small delay between batches to avoid overwhelming the server
        if (i + concurrencyLimit < workOrders.length) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }

      // Also prefetch day destinations
      try {
        const dayDestinations = await workOrderService.getDayDestinations(technicianId, today)
        await workOrderOfflineStorage.saveDayDestinationsOffline(technicianId, today, dayDestinations)
      } catch (error) {
        // Don't fail the entire prefetch if day destinations fail
      }

      // Log results
      const successful = results.filter(r => r.status === 'fulfilled').length
      const failed = results.filter(r => r.status === 'rejected').length


      // Update last prefetch timestamp
      this.lastPrefetchTimestamps.set(cacheKey, Date.now())

    } catch (error) {
    } finally {
      this.isPrefetching = false
      this.prefetchAbortController = null
    }
  }

  /**
   * Check if a prefetch is needed based on the last timestamp
   */
  private shouldPrefetch(cacheKey: string): boolean {
    const lastPrefetch = this.lastPrefetchTimestamps.get(cacheKey)
    if (!lastPrefetch) {
      return true // Never prefetched before
    }

    const timeSinceLastPrefetch = Date.now() - lastPrefetch
    return timeSinceLastPrefetch >= this.prefetchInterval
  }

  /**
   * Prefetch individual work order details and cache them
   */
  private async prefetchWorkOrderDetails(workOrderId: number): Promise<void> {
    try {
      // Note: For prefetching, we still use fetch directly to support abort signals
      // since apiService doesn't currently support abort signals
      const response = await fetch(apiEndpoints.workOrders.details(workOrderId), {
        signal: this.prefetchAbortController?.signal
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch work order ${workOrderId}: ${response.status}`)
      }

      const data = await response.json()

      // Save to offline storage
      await workOrderOfflineStorage.saveWorkOrderOffline(workOrderId, data)

    } catch (error) {
      // Don't throw, just log the error so other work orders can continue
    }
  }

  /**
   * Start automatic refresh for a technician
   * Sets up a 30-minute interval to refresh work order cache
   */
  startAutoRefresh(technicianId: number): void {
    const today = new Date().toLocaleString('sv').split(' ')[0]
    const cacheKey = `${technicianId}-${today}`
    
    // Clear existing timer if any
    this.stopAutoRefresh(cacheKey)
    
    // Set up new interval
    const timer = setInterval(() => {
      if (navigator.onLine) {
        this.prefetchTodayWorkOrders(technicianId, true) // Force refresh
          .catch(() => {})
      }
    }, this.prefetchInterval)
    
    this.intervalTimers.set(cacheKey, timer)
  }

  /**
   * Stop automatic refresh for a specific cache key
   */
  stopAutoRefresh(cacheKey: string): void {
    const timer = this.intervalTimers.get(cacheKey)
    if (timer) {
      clearInterval(timer)
      this.intervalTimers.delete(cacheKey)
    }
  }

  /**
   * Stop all automatic refreshes
   */
  stopAllAutoRefresh(): void {
    this.intervalTimers.forEach((timer, cacheKey) => {
      clearInterval(timer)
    })
    this.intervalTimers.clear()
  }

  /**
   * Cancel any ongoing prefetch operation
   */
  cancelPrefetch(): void {
    // Ignore cancellation requests that happen too quickly after starting (React strict mode)
    const timeSinceStart = Date.now() - this.prefetchStartTime
    if (timeSinceStart < 1000) { // Less than 1 second
      return
    }
    
    if (this.prefetchAbortController) {
      this.prefetchAbortController.abort()
    }
  }

  /**
   * Check if prefetch is currently running
   */
  isCurrentlyPrefetching(): boolean {
    return this.isPrefetching
  }

  /**
   * Get the last prefetch timestamp for a technician and date
   */
  getLastPrefetchTime(technicianId: number, date: string): number | null {
    const cacheKey = `${technicianId}-${date}`
    return this.lastPrefetchTimestamps.get(cacheKey) || null
  }

  /**
   * Get time until next scheduled prefetch in milliseconds
   */
  getTimeUntilNextPrefetch(technicianId: number, date: string): number {
    const lastPrefetch = this.getLastPrefetchTime(technicianId, date)
    if (!lastPrefetch) {
      return 0 // Should prefetch immediately
    }
    
    const timeSinceLastPrefetch = Date.now() - lastPrefetch
    const timeUntilNext = this.prefetchInterval - timeSinceLastPrefetch
    return Math.max(0, timeUntilNext)
  }
}

export const workOrderPrefetchService = new WorkOrderPrefetchService()